"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

const DEFAULT_IMAGE =
	"/image/new-version/molding/overmolding/material-combinations-for-overmolding-technology.webp";

const TABLE_ROW_INDICES = [1, 2, 3, 4, 5, 6, 7, 8] as const;

export interface OvermoldingMaterialCombinationsProps {
	/** 命名空间需含 title、intro、各小节标题与正文、表头、row1～row8 各四列 */
	translationNamespace?: string;
	/** 左侧配图，默认 material-combinations-for-overmolding-technology.webp */
	imageSrc?: string;
}

/**
 * Material Combinations：顶栏全宽标题+引言；
 * lg+ 单网格 10 列——上排左 4/10（图 + Functional Synergy）、右 6/10（表）；下排左 5/10（Performance）、右 5/10（Sustainable），与稿图 2/5|3/5 与 1/2|1/2 对齐。
 */
export default function OvermoldingMaterialCombinations({
	translationNamespace = "overmoldingMaterialCombinationsSection",
	imageSrc = DEFAULT_IMAGE,
}: OvermoldingMaterialCombinationsProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="w-full bg-[#F6F6F6] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 全宽标题区 */}
				<header className="mb-6 sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="mt-3 text-sm leading-relaxed text-gray-900 sm:mt-4 md:text-base">{t("intro")}</p>
				</header>

				{/*
					lg: 10 列网格——与视觉稿一致：
					· 第一行：左 4/10（2/5）图+Synergy，右 6/10（3/5）表格
					· 第二行：左 5/10、右 5/10，Performance 与左列 Synergy 左缘对齐
				*/}
				<div className="grid grid-cols-1 gap-10 lg:grid-cols-10 lg:gap-x-8 lg:gap-y-12 xl:gap-x-10">
					{/* —— 上排左：2/5 —— */}
					<div className="min-w-0 lg:col-span-4">
						<div className="relative aspect-[4/3] w-full overflow-hidden bg-[#f0f0f0] shadow-sm">
							<Image
								src={imageSrc}
								alt={t("imageAlt")}
								fill
								className="object-cover"
								sizes="(max-width: 1024px) 100vw, 40vw"
							/>
						</div>
						<div className="mt-8 md:mt-10">
							<h3 className="text-base font-bold text-themePrimary400 sm:text-lg">{t("synergyTitle")}</h3>
							<p className="mt-2 text-sm leading-relaxed text-black md:text-base">{t("synergyDesc")}</p>
						</div>
					</div>

					{/* —— 上排右：3/5 表格 —— */}
					<div className="min-w-0 lg:col-span-6">
						<div className="overflow-x-auto border border-gray-200">
							<table className="w-full min-w-[640px] border-collapse text-left text-sm sm:min-w-[680px] sm:text-sm">
								<thead>
									<tr className="bg-themePrimary400 text-white">
										<th className="border border-white/25 px-3 py-4 font-semibold sm:px-4 sm:py-4">
											{t("thSubstrate")}
										</th>
										<th className="border border-white/25 px-3 py-4 font-semibold sm:px-4 sm:py-4">
											{t("thOvermold")}
										</th>
										<th className="border border-white/25 px-3 py-4 font-semibold sm:px-4 sm:py-4">
											{t("thApplications")}
										</th>
										<th className="border border-white/25 px-3 py-4 font-semibold sm:px-4 sm:py-4">
											{t("thCompliance")}
										</th>
									</tr>
								</thead>
								<tbody>
									{TABLE_ROW_INDICES.map((rowNum, index) => (
										<tr key={rowNum} className="text-sm">
											<td className="border border-gray-200 px-3 py-2.5 font-medium text-black sm:px-4 sm:py-4">
												{t(`row${rowNum}Substrate`)}
											</td>
											<td className="border border-gray-200 px-3 py-2.5 text-black sm:px-4 sm:py-3.5">
												{t(`row${rowNum}Overmold`)}
											</td>
											<td className="border border-gray-200 px-3 py-2.5 text-black sm:px-4 sm:py-3.5">
												{t(`row${rowNum}Applications`)}
											</td>
											<td className="border border-gray-200 px-3 py-2.5 text-black sm:px-4 sm:py-3.5">
												{t(`row${rowNum}Compliance`)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>

					{/* —— 下排左：1/2 —— */}
					<div className="min-w-0 lg:col-span-5">
						<h3 className="text-base font-bold text-themePrimary400 sm:text-lg">{t("performanceTitle")}</h3>
						<p className="mt-2 text-sm leading-relaxed text-black md:text-base">{t("performanceDesc")}</p>
					</div>

					{/* —— 下排右：1/2 —— */}
					<div className="min-w-0 lg:col-span-5">
						<h3 className="text-base font-bold text-themePrimary400 sm:text-lg">{t("sustainableTitle")}</h3>
						<p className="mt-2 text-sm leading-relaxed text-black md:text-base">{t("sustainableDesc")}</p>
					</div>
				</div>
			</div>
		</section>
	);
}
